/**
 * Realm (lobby) protocol: JSON messages over the `/realm` WebSocket.
 * Mirrors the shape of classic Battle.net: account → character → chat channel → games.
 */

import type { DataProfile } from './data-profile';

export const PROTOCOL_VERSION = 1;
export const REALM_PORT = 6112;

export type ClassCode = 'ama' | 'sor' | 'nec' | 'pal' | 'bar' | 'dru' | 'ass';
export const CLASS_CODES: ClassCode[] = ['ama', 'sor', 'nec', 'pal', 'bar', 'dru', 'ass'];
/** charstats.txt row order. */
export const CLASS_NAMES: Record<ClassCode, string> = {
  ama: 'Amazon',
  sor: 'Sorceress',
  nec: 'Necromancer',
  pal: 'Paladin',
  bar: 'Barbarian',
  dru: 'Druid',
  ass: 'Assassin',
};
/** Two-letter animation tokens used in file paths (data\global\chars\<TK>\...). */
export const CLASS_TOKENS: Record<ClassCode, string> = { ama: 'AM', sor: 'SO', nec: 'NE', pal: 'PA', bar: 'BA', dru: 'DZ', ass: 'AI' };

export type Difficulty = 0 | 1 | 2;
export const DIFFICULTY_NAMES = ['Normal', 'Nightmare', 'Hell'] as const;

/** Visible equipment for front-end portraits: component → armor/weapon token (e.g. TR: "lit"). */
export type Appearance = Partial<Record<'HD' | 'TR' | 'LG' | 'RA' | 'LA' | 'RH' | 'LH' | 'SH' | 'S1' | 'S2', string>> & { wclass?: string };

export interface CharSummary {
  name: string;
  cls: ClassCode;
  level: number;
  hardcore: boolean;
  expansion: boolean;
  /** Ladder character (patch 1.10): plays ladder games only, until the season ends. */
  ladder?: boolean;
  dead: boolean;
  /** Highest difficulty unlocked. */
  maxDifficulty: Difficulty;
  /** Title earned by progression ("Slayer", "Champion"…), derived from completed difficulties. */
  title: string;
  appearance: Appearance;
  lastPlayed: number;
}

export interface ChannelUser {
  name: string; // *account
  char?: { name: string; cls: ClassCode; level: number; hardcore: boolean; expansion: boolean; appearance?: Appearance };
  flags: number; // UserFlag bits
  ping: number;
}

export const enum UserFlag {
  Operator = 1,
  Away = 2,
  DoNotDisturb = 4,
  Squelched = 8,
  InGame = 16,
}

export interface GameListing {
  id: string;
  name: string;
  description: string;
  difficulty: Difficulty;
  players: number;
  maxPlayers: number;
  hasPassword: boolean;
  hardcore: boolean;
  expansion: boolean;
  /** Realm ladder game (ladder-only runewords, uniques and recipes) */
  ladder?: boolean;
  levelRange: [number, number] | null;
  createdAt: number;
  creator: string;
}

export interface GameDetail extends GameListing {
  members: { name: string; cls: ClassCode; level: number }[];
  uptime: number;
}

export type ChatKind = 'talk' | 'emote' | 'whisperFrom' | 'whisperTo' | 'info' | 'error' | 'broadcast' | 'friend' | 'channel';

// ---- Client → server -------------------------------------------------------

/** One dated announcement ("Wednesday, March 28, 2012" + text). `date` is an ISO date. */
export interface NewsItem {
  date: string;
  text: string;
}

export interface LadderEntry {
  rank: number;
  name: string;
  cls: ClassCode;
  level: number;
  expansion: boolean;
  dead: boolean;
}

export type RealmRequest =
  /**
   * First message. `dataHash`: whole-table hash (legacy, tools); `data`: the client's
   * `DataProfile`, checked part by part. Answered by `ready` or a `dataMismatch`/`badVersion`
   * error, after which the realm closes the connection.
   */
  | { t: 'hello'; version: number; dataHash: string; client: string; data?: DataProfile }
  /** `email`: optional, for password recovery (ignored when the realm sends no email). */
  | { t: 'createAccount'; name: string; password: string; email?: string }
  | { t: 'login'; name: string; password: string }
  | { t: 'changePassword'; oldPassword: string; newPassword: string }
  /** The logged-in account's own email settings (answered by `accountInfo`). */
  | { t: 'accountInfo' }
  /** Sets the account's email; '' removes it. Needs the current password. Answered by `accountInfo`. */
  | { t: 'setEmail'; password: string; email: string }
  /**
   * Asks for a password reset by account name or email. Always answered by the same `done`
   * message whether or not the account or address exists.
   */
  | { t: 'forgotPassword'; name: string }
  /** Sets a new password with a code from a reset message (link or typed). Answered by `done`. */
  | { t: 'resetPassword'; code: string; password: string }
  /** Confirms an email address with the code from its confirmation message. Answered by `done`. */
  | { t: 'verifyEmail'; code: string }
  | { t: 'listChars' }
  | { t: 'createChar'; name: string; cls: ClassCode; hardcore: boolean; expansion: boolean; ladder?: boolean }
  | { t: 'deleteChar'; name: string }
  /** Makes a classic character an expansion character (one way, as on Battle.net). Answered by `chars`. */
  | { t: 'convertChar'; name: string }
  /** The realm's top characters of a ladder (answered by `ladder`). */
  | { t: 'ladder'; hardcore: boolean }
  | { t: 'selectChar'; name: string }
  | { t: 'enterChat' }
  | { t: 'chat'; text: string }
  | { t: 'joinChannel'; name: string }
  | { t: 'listChannels' }
  | { t: 'listGames'; filter?: string }
  | { t: 'gameDetail'; id: string }
  | { t: 'createGame'; name: string; password: string; description: string; difficulty: Difficulty; maxPlayers: number; levelDiff: number | null }
  | { t: 'joinGame'; name: string; password: string }
  | { t: 'ping'; n: number }
  /** Gives up a place in the line for a full realm (see the `queue` event). */
  | { t: 'leaveQueue' }
  | { t: 'logout' };

// ---- Server → client -------------------------------------------------------

export type RealmEvent =
  /** `ladder`: a ladder season is running (characters may be created as ladder characters). */
  /** `mail`: the realm sends email, so accounts may add one and reset a forgotten password. */
  /** `news`: dated announcements, newest first, for the lobby's "Battle.net News Updates". */
  | { t: 'welcome'; realm: string; motd: string[]; online: number; games: number; ladder?: boolean; mail?: boolean; news?: NewsItem[] }
  /** The client's hello was accepted; `version` is the game version the realm plays ("1.14d"). */
  | { t: 'ready'; version: string }
  | { t: 'error'; code: RealmError; msg: string; req?: RealmRequest['t'] }
  | { t: 'loggedIn'; account: string }
  /**
   * The password was right but the realm is full: the account waits in line. Sent when the
   * position changes (at most every few seconds); `loggedIn` follows on admission. `etaSec`:
   * estimated wait at the recent admission rate, null until there is one. Only `ping`,
   * `leaveQueue` and `logout` are answered while waiting.
   */
  | { t: 'queue'; position: number; length: number; etaSec: number | null }
  | { t: 'accountCreated'; account: string }
  /** The account's own email (never shown to anyone else); `msg` says what just changed. */
  | { t: 'accountInfo'; email: string | null; verified: boolean; msg?: string }
  /** A request without another answer succeeded; `msg` is for the player. */
  | { t: 'done'; req: RealmRequest['t']; msg: string }
  | { t: 'chars'; chars: CharSummary[]; maxChars: number }
  | { t: 'charSelected'; char: CharSummary }
  | { t: 'channel'; name: string; users: ChannelUser[] }
  | { t: 'channels'; channels: { name: string; users: number }[] }
  | { t: 'userJoin'; user: ChannelUser }
  | { t: 'userLeave'; name: string }
  | { t: 'userUpdate'; user: ChannelUser }
  | { t: 'chatMsg'; kind: ChatKind; from?: string; text: string }
  | { t: 'games'; games: GameListing[] }
  | { t: 'gameDetail'; game: GameDetail }
  /** Top characters of the requested ladder, best first (by level, then who got there first). */
  | { t: 'ladder'; hardcore: boolean; season: boolean; entries: LadderEntry[] }
  | { t: 'gameTicket'; ticket: string; game: GameListing; url: string }
  | { t: 'pong'; n: number; serverTime: number };

export type RealmError =
  | 'badVersion'
  | 'dataMismatch'
  | 'badName'
  | 'nameTaken'
  | 'badPassword'
  | 'noAccount'
  | 'alreadyOnline'
  | 'notLoggedIn'
  | 'noChar'
  | 'charLimit'
  | 'gameExists'
  | 'gameFull'
  | 'gameNotFound'
  | 'wrongPassword'
  | 'levelRange'
  | 'difficultyLocked'
  | 'hardcoreMismatch'
  | 'deadHardcore'
  | 'rateLimited'
  | 'banned'
  | 'serverBusy'
  | 'badEmail'
  | 'badCode'
  | 'invalid';

/** Account and character name rules (classic Battle.net-like). */
export const ACCOUNT_NAME = /^[A-Za-z0-9][A-Za-z0-9_\-.[\]]{1,14}$/;
export const CHAR_NAME = /^[A-Za-z][A-Za-z_-]{1,14}$/;
export const GAME_NAME = /^[\x20-\x7e]{1,15}$/;
/** Email addresses the realm accepts: a plain local@domain.tld. */
export const EMAIL = /^[^\s@<>()",;:\\[\]]{1,64}@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/i;

/** An email as stored and compared: trimmed and lower-cased; null when empty, undefined when invalid. */
export function normalizeEmail(raw: unknown): string | null | undefined {
  if (typeof raw !== 'string') return undefined;
  const e = raw.trim().toLowerCase();
  if (!e) return null;
  return e.length <= 254 && EMAIL.test(e) ? e : undefined;
}
